import { NavLink } from "react-router-dom";

import { AppendixIntro } from "../components/AppendixIntro";
import { DataTableCard } from "../components/DataTableCard";
import { MetricCard } from "../components/MetricCard";
import { NumberedListCard } from "../components/NumberedListCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type SiteZoningPageData = Awaited<ReturnType<typeof loadSiteZoningPage>>;

async function loadSiteZoningPage() {
  const [site, parcel, zoning] = await Promise.all([api.getSite(), api.getParcel(), api.getZoning()]);
  return { site, parcel, zoning };
}

function formatCurrency(value: number) {
  return `$${value.toLocaleString()}`;
}

function formatBoolean(value: boolean, positive = "Yes", negative = "No") {
  return value ? positive : negative;
}

function SiteZoningContent({ data }: { data: SiteZoningPageData }) {
  const { site, parcel, zoning } = data;
  const { keyFacts, siteSummary } = site;
  const combinedGateCount = siteSummary.openItems.length + parcel.openItems.length + zoning.openItems.length;

  const parcelFactsRows = [
    ["Property", parcel.keyFacts.propertyAddress],
    ["Parcel ID", parcel.keyFacts.parcelId],
    ["Zoning district", `${zoning.keyFacts.zoning} | ${zoning.keyFacts.zoningDescription}`],
    [
      "Lot area",
      `${parcel.keyFacts.parcelArea.squareFeet.toLocaleString()} sf | ${parcel.keyFacts.parcelArea.acres} acres`
    ],
    ["Lot geometry", `${parcel.keyFacts.frontageFeet} ft frontage | ${parcel.keyFacts.depthFeet} ft depth`],
    ["Property class", parcel.keyFacts.propertyClass],
    ["Assessed value", formatCurrency(parcel.keyFacts.taxAssessedValue)],
    ["County / coordinates", `${parcel.keyFacts.county} | ${parcel.keyFacts.coordinates}`]
  ];

  const fitMatrixRows = [
    ["Overall site posture", siteSummary.overallPosture, "Top-line land and entitlement read remains workable, not closed"],
    ["Lead concept fit", keyFacts.leadConcept, siteSummary.leadConceptFit],
    [
      "Flood / slope",
      `${siteSummary.floodStatus.label} / ${siteSummary.slopeStatus.label}`,
      `${siteSummary.floodStatus.summary} ${siteSummary.slopeStatus.summary}`
    ],
    ["GIS flags", siteSummary.gisFlagStatus.label, siteSummary.gisFlagStatus.summary],
    ["Parcel conclusion", parcel.status, parcel.conclusion],
    ["Regulatory conclusion", zoning.status, zoning.conclusion]
  ];

  const zoningUtilityRows = [
    ["Parcel application", zoning.parcelApplicationBestRead, "Parcel-specific code reading still controls execution"],
    ["Utility posture", siteSummary.utilityStatus.label, siteSummary.utilityStatus.summary],
    [
      "City service",
      `${formatBoolean(keyFacts.cityWaterAvailable)} water | ${formatBoolean(keyFacts.citySewerAvailable)} sewer`,
      "Known public utility availability"
    ],
    ["City-side baseline", formatCurrency(siteSummary.utilityStatus.citySideBaseline), "Current known public utility anchor"],
    ["Access / parking", siteSummary.parkingAccessStatus.label, siteSummary.parkingAccessStatus.summary],
    [
      "Parking requirement",
      formatBoolean(keyFacts.offStreetParkingRequired, "Required", "Not required"),
      "Off-street parking burden remains parcel-application sensitive"
    ],
    [
      "STR registration",
      formatBoolean(zoning.keyFacts.strRegistrationRequired, "Required", "Not required"),
      `${formatCurrency(zoning.keyFacts.strRegistrationFee)} fee | ${formatCurrency(zoning.keyFacts.failureToRegisterPenalty)} penalty exposure`
    ],
    ["Occupancy / building code", zoning.keyFacts.occupancyRule, zoning.keyFacts.buildingCodeRequirement]
  ];

  const deeperDetailRows = [
    ["Rental inspection district", zoning.keyFacts.rentalInspectionDistrictStatus, "Operational compliance still matters"],
    ["Related occupancy rule", zoning.keyFacts.relatedOccupancyRule, "Use with parcel-specific application discipline"],
    ["Inspection approach", zoning.keyFacts.inspectionApproach, "Execution still runs through permitting and inspection"],
    ["Fallback concept", keyFacts.fallbackConcept, "Backup path stays visible if the lead stack weakens"]
  ];

  return (
    <div className="stack-xl">
      <AppendixIntro
        eyebrow="Appendix / Site & Zoning"
        title="Compact land and regulatory support."
        body="This page now does one job first: show whether parcel fit, zoning posture, utility readiness, and access stay inside a manageable execution box. The deeper parcel and code detail remains available, but it no longer leads the read."
        noteTitle="How to use this appendix"
        note="Use this after Investment Case and Financials. Confirm the parcel and rule set still support the lead concept, then drop into parcel or zoning detail only if a land-side gate still needs closer review."
        actions={
          <>
            <NavLink to="/investment-case" className="link-button link-button-secondary">
              Investment Case
            </NavLink>
            <NavLink to="/financials" className="link-button link-button-secondary">
              Financials
            </NavLink>
          </>
        }
      />

      <div
        style={{
          display: "grid",
          gap: "16px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
        }}
      >
        <MetricCard title="Overall fit" value={siteSummary.overallPosture} detail="Top-line parcel and entitlement posture" />
        <MetricCard title="Zoning district" value={keyFacts.zoning} detail={zoning.keyFacts.zoningDescription} />
        <MetricCard title="Utility posture" value={siteSummary.utilityStatus.label} detail="Known public service posture and utility anchor" />
        <MetricCard title="Open gates" value={`${combinedGateCount}`} detail="Finite land-side and compliance items still open" />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Parcel Facts"
          title="Identity and geometry"
          description="The parcel basics should clear in one pass before any deeper land-side review begins."
          columns={["Field", "Current read"]}
          rows={parcelFactsRows}
        />
        <DataTableCard
          eyebrow="Fit Matrix"
          title="Parcel, physical, and regulatory fit"
          description="The current support question is whether the lead concept still fits the site and rule set. This table keeps that answer compact."
          columns={["Layer", "Current read", "Investor meaning"]}
          rows={fitMatrixRows}
        />
      </div>

      <DataTableCard
        eyebrow="Zoning / Utility / Access"
        title="Operational land-side matrix"
        description="Utility, access, and code items remain visible here, but the page now treats them as support detail rather than the opening investor narrative."
        columns={["Layer", "Current read", "Constraint / note"]}
        rows={zoningUtilityRows}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
        }}
      >
        <NumberedListCard
          eyebrow="Land-side Gates"
          title="Remaining parcel and physical items"
          description="These stay visible because they still control execution discipline, but they no longer dominate the page."
          items={[...siteSummary.openItems, ...parcel.openItems]}
          tone="neutral"
        />
        <NumberedListCard
          eyebrow="Compliance Gates"
          title="Remaining zoning and code items"
          description="The regulatory open items stay explicit and compact."
          items={zoning.openItems}
          tone="warning"
        />
      </div>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div className="stack-md">
          <div className="stack-xs">
            <span className="card-eyebrow">Deeper Appendix Detail</span>
            <h3 className="card-title">Lower-priority parcel and rule detail stays available below the summary layer.</h3>
            <p className="card-copy">
              This lower section keeps the remaining land-side detail accessible without letting parcel granularity compete with the primary investor path.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
            }}
          >
            <DataTableCard
              eyebrow="Further Detail"
              title="Additional land and compliance reads"
              columns={["Layer", "Current read", "Meaning"]}
              rows={deeperDetailRows}
            />

            <div
              style={{
                display: "grid",
                gap: "14px"
              }}
            >
              {[
                {
                  src: "/comps/104-f-st-map.jpg",
                  alt: "Local map context near 400 Norwood",
                  label: "Map context",
                  note: "Grounded location context remains useful, but it now sits below the summary read."
                },
                {
                  src: "/comps/104-f-st-streetview.jpg",
                  alt: "Street context near 400 Norwood",
                  label: "Street context",
                  note: "Street-level context helps confirm compact infill fit without turning this page into a parcel report."
                }
              ].map((item) => (
                <figure
                  key={item.src}
                  style={{
                    margin: 0,
                    overflow: "hidden",
                    borderRadius: "18px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <img src={item.src} alt={item.alt} style={{ display: "block", width: "100%", height: "160px", objectFit: "cover" }} />
                  <figcaption style={{ padding: "14px 16px" }}>
                    <div style={{ fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(191, 207, 229, 0.62)" }}>
                      {item.label}
                    </div>
                    <div style={{ marginTop: "8px", color: "rgba(232, 239, 250, 0.88)", lineHeight: 1.5 }}>{item.note}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="row-wrap gap-sm">
            <NavLink to="/parcel" className="link-button link-button-secondary">
              Parcel detail
            </NavLink>
            <NavLink to="/zoning" className="link-button link-button-secondary">
              Zoning detail
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export function SitePage() {
  const { data, loading, error } = useContent("site-zoning-page", loadSiteZoningPage);

  return (
    <PageContainer
      eyebrow="Appendix / Site & Zoning"
      title="Compact land and regulatory support."
      lede="This appendix screen keeps parcel fit, zoning posture, utility readiness, and land-side gates visible without letting site diligence compete with the primary investor pages."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading site and zoning...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <SiteZoningContent data={data} /> : null}
    </PageContainer>
  );
}
